// Design tokens for the Athens Data Portal.
//
// Every value here was already in the codebase — this file just gives the
// ~80 hardcoded hex literals scattered across the pages a single home. The
// grey ramp is Tailwind's, which is what the original inline styles were
// reaching for by hand (#9CA3AF appeared 172 times, #E5E7EB 153).
//
// Usage: import { c, t, sp, r, sh } from '../theme';
//   <div style={{ color: c.textMuted, padding: sp[4] }}>
//
// Adopt incrementally. A page that still uses literals renders identically —
// the tokens ARE the literals — so pages can be converted one at a time.

// ── Color ──────────────────────────────────────────────────────────
export const c = {
  // Brand. Red is sampled from Athens-Logo.png; navy is the sidebar.
  brand:        '#D1232A',
  brandDark:    '#8E1A1F',
  brandTint:    '#FEE2E2',   // red wash for error surfaces
  navy:         '#1B2A4A',
  navyLight:    '#24365C',   // raised navy — gradients, hover on sidebar
  navyDeep:     '#131E36',   // recessed navy — gradient far end

  // Navy-on-dark text, used inside the sidebar and on navy grounds.
  onNavy:       '#E8EEF7',
  onNavyMuted:  '#94A8C0',
  onNavyFaint:  '#7A90B3',
  onNavyLabel:  '#4A6080',

  // Neutral ramp.
  text:         '#111827',
  textStrong:   '#374151',
  textMuted:    '#6B7280',
  textFaint:    '#9CA3AF',
  border:       '#E5E7EB',
  borderStrong: '#D1D5DB',
  surface:      '#FFFFFF',
  surfaceSunk:  '#F9FAFB',
  canvas:       '#F3F4F6',

  // Semantic — status pills, deltas, validation. Kept separate from the
  // brand red on purpose: a failing metric is not a brand moment.
  success:      '#16A34A',
  successText:  '#166534',
  successTint:  '#DCFCE7',
  warning:      '#A16207',
  warningText:  '#92400E',
  warningTint:  '#FEF3C7',
  danger:       '#DC2626',
  dangerText:   '#B91C1C',
  dangerTint:   '#FEE2E2',
  info:         '#1D4ED8',
  infoTint:     '#EFF6FF',
};

// ── Type scale ─────────────────────────────────────────────────────
// A fixed ladder, so sizes stop drifting between 12.5px and 13px across
// pages. `mono` is for anything with aligned digits — see tabular below.
export const t = {
  family:     "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  familyMono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",

  // size / line-height pairs
  display:  { fontSize: '30px', lineHeight: 1.2,  letterSpacing: '-0.02em', fontWeight: 650 },
  // 22px because that is what 13 of the 15 page titles already used; the two
  // at 24px were the outliers. Matching the token to reality avoids a visible
  // size change on every page just to satisfy an invented scale.
  h1:       { fontSize: '22px', lineHeight: 1.25, letterSpacing: '-0.02em', fontWeight: 650 },
  h2:       { fontSize: '18px', lineHeight: 1.3,  letterSpacing: '-0.01em', fontWeight: 600 },
  h3:       { fontSize: '15px', lineHeight: 1.4,  fontWeight: 600 },
  body:     { fontSize: '14px', lineHeight: 1.55 },
  bodySm:   { fontSize: '13px', lineHeight: 1.5 },
  caption:  { fontSize: '12px', lineHeight: 1.45 },
  micro:    { fontSize: '11px', lineHeight: 1.4 },
  label:    { fontSize: '10px', lineHeight: 1.4, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.12em' },

  // Spread onto any element whose digits line up in a column.
  tabular:  { fontVariantNumeric: 'tabular-nums' },
};

// ── Spacing ────────────────────────────────────────────────────────
// 4px base. Index = multiples, so sp[4] is 16px.
export const sp = ['0', '4px', '8px', '12px', '16px', '20px', '24px',
                   '28px', '32px', '40px', '48px', '64px'];

// ── Radius ─────────────────────────────────────────────────────────
export const r = {
  sm:   '4px',
  md:   '6px',
  lg:   '8px',
  xl:   '12px',
  '2xl':'16px',
  pill: '999px',
};

// ── Stacking order ─────────────────────────────────────────────────
// These four numbers only mean anything RELATIVE to each other, and they are
// set in two different places — AppShell's `<style>` block for the shell, and
// the inline styles of the dropdown components. Holding them apart is what
// made the Contact Center filter menus open UNDERNEATH the sidebar: the menus
// were 40, the sidebar 100, and nothing in between the two files said they
// were competing. `.sh-content` creates no stacking context (no overflow, no
// z-index, no transform), so a menu inside it is compared directly against
// the sidebar rather than being trapped in its own layer.
//
// So: a floating layer that is OPENED BY something must sit above every layer
// that is merely part of the frame. `menu` above `sidebar` is the invariant
// that was broken, and tests/themeStacking.test.jsx holds it.
export const z = {
  overlay: 99,    // the mobile nav's backdrop
  sidebar: 100,   // the nav itself, sticky, full height
  menu:    200,   // any dropdown, popover or flyout a control opens
};

// ── Charts ─────────────────────────────────────────────────────────
// Two categorical sets, because the right test depends on the chart form.
//
// `categorical` — for forms where series sit in a fixed order and only
// neighbours touch (stacked areas, stacked bars). Validated adjacent-pairs
// against the white card surface:
//     lightness PASS · chroma PASS
//     CVD        PASS — worst adjacent #eda100↔#1baf7a ΔE 9.1 (protan)
//     normal     PASS — worst adjacent #e87ba4↔#eda100 ΔE 19.6
//     contrast   WARN — aqua/yellow/magenta under 3:1, so these charts must
//                       ship a legend or an adjacent table view. They do.
//
// `coVisible` — for forms where every class is comparable with every other
// at once (pie, donut, scatter). All-pairs is the correct test there and it
// is far stricter: the 7-slot set FAILS it (#008300↔#eb6834 ΔE 3.2 protan),
// so co-visible forms get four slots, validated --pairs all:
//     CVD    PASS — worst #1baf7a↔#eb6834 ΔE 9.2 (deutan)
//     normal PASS — worst #4a3aa7↔#2a78d6 ΔE 16.3
//
// Four is a ceiling, not a preference. Past it, fold the tail into `other`
// rather than generating a fifth hue — a donut with eight slices cannot be
// made colourblind-safe, only smaller.
export const chart = {
  categorical: [
    '#2a78d6', // blue
    '#eb6834', // orange
    '#1baf7a', // aqua
    '#eda100', // yellow
    '#e87ba4', // magenta
    '#008300', // green
    '#4a3aa7', // violet
  ],
  coVisible: ['#2a78d6', '#eb6834', '#1baf7a', '#4a3aa7'],
  other:     '#9CA3AF',

  // Single hue, light→dark, for magnitude. Assigned from the dark end so the
  // most recent period is always the deepest navy.
  sequential: ['#9CB4D2', '#6E8CB8', '#41618F', '#1B2A4A'],

  // Selection emphasis on single-series bar charts: the focused bar reads
  // navy, the rest recede. Not a categorical scale — length is the message.
  emphasis:   '#1B2A4A',
  recessive:  '#4A6080',
  deselected: '#9AA7BC',
  deselectedLight: '#CBD3DF',

  // ── Shift timeline (Time and Wage Audit) ─────────────────────────────────
  // The two ends of a shift are separate classes a reader compares directly,
  // so they come from coVisible (all-pairs validated) rather than categorical.
  // Verified with the palette validator against the green worked bar they are
  // drawn on top of: worst all-pairs separation is 13.0 deutan / 16.3 normal,
  // where 8 is the target. Violet rather than orange for the start: orange
  // against the same green only reached 7.4 deutan, inside the floor band.
  //
  // Fills are the same hue at 6% so the border carries identity and the fill
  // never competes with the worked bar underneath it. Expressed as tokens
  // because the alternative is an rgba() literal in three components, which is
  // how 1,177 hardcoded hexes happened the first time.
  shiftStart:     '#4a3aa7',
  shiftStartFill: 'rgba(74, 58, 167, 0.06)',
  shiftEnd:       '#2a78d6',
  shiftEndFill:   'rgba(42, 120, 214, 0.06)',
  // Over threshold is a STATE, not an identity, so it keeps the status red at
  // both ends and is never a third hue in the legend's identity group.
  overFill:       'rgba(220, 38, 38, 0.07)',

  // Chart furniture — deliberately recessive.
  grid:          '#F0F1F3',
  axisText:      '#9CA3AF',
  axisTextStrong:'#374151',
  tooltipBorder: '#E5E7EB',
  surface:       '#FFFFFF',
};

// ── Tables ─────────────────────────────────────────────────────────
// Nineteen files render a table and between them defined ten different
// `th` styles and eleven different `td` styles. Almost every one expressed
// the same intent — left-aligned uppercase micro-label header on a sunk
// background, comfortable body cell — and differed only in padding, which
// ranged from 4px to 10px vertical with no pattern behind it.
//
// Two densities are real and worth keeping: `md` for a page's primary table,
// `sm` for the dense sub-tables nested inside drill-down panels. Everything
// else was drift.
//
// Spread these into a file's existing style object rather than replacing its
// markup:  th: { ...tbl.th, position: 'sticky', top: 0 }
const thBase = {
  textAlign:     'left',
  fontSize:      '10px',
  fontWeight:    600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color:         c.textFaint,
  borderBottom:  `1px solid ${c.border}`,
  background:    c.surfaceSunk,
};
const tdBase = {
  color:        c.textStrong,
  verticalAlign:'middle',
};

export const tbl = {
  th:     { ...thBase, padding: '9px 14px' },
  thSm:   { ...thBase, padding: '7px 10px' },
  thR:    { ...thBase, padding: '9px 14px', textAlign: 'right' },
  thRSm:  { ...thBase, padding: '7px 10px', textAlign: 'right' },
  // Centered to match a centered data column (a skill score, a calculated
  // rating) — a left-aligned label over centered data visibly drifts apart
  // once the label wraps to two or three lines and widens the column.
  thC:    { ...thBase, padding: '9px 14px', textAlign: 'center' },
  thCSm:  { ...thBase, padding: '7px 10px', textAlign: 'center' },

  td:     { ...tdBase, padding: '9px 14px' },
  tdSm:   { ...tdBase, padding: '6px 10px' },
  // Numeric cells right-align and use lining figures so columns compare.
  tdR:    { ...tdBase, padding: '9px 14px', textAlign: 'right',
            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  tdRSm:  { ...tdBase, padding: '6px 10px', textAlign: 'right',
            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },

  tr:     { borderBottom: `1px solid ${c.canvas}` },
  trHover:{ borderBottom: `1px solid ${c.canvas}`, cursor: 'pointer' },
  // Alternating row background -- call with the row's index (0-based) so
  // dense tables read as banded groups of data instead of one flat wall of
  // same-color rows (Jesse, Nov 2026: "too much data in one color").
  trZebra: (i) => ({ borderBottom: `1px solid ${c.canvas}`, background: i % 2 ? c.surfaceSunk : c.surface }),
};

// ── Cards ──────────────────────────────────────────────────────────
// Card chrome was already near-identical everywhere — white, 1px #E5E7EB,
// rounded — and split only between a 12px radius for page-level cards and
// 10px for the smaller cards that hold a chart. Both are kept.
//
// Padding is deliberately NOT part of these tokens. It legitimately varies
// with content: a chart card trims its bottom padding so the axis labels sit
// correctly, a form card wants a roomy 24px. Spread the chrome and keep the
// padding local:  card: { ...card.base, padding: '24px' }
export const card = {
  base: { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px' },
  sm:   { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '10px' },
  sunk: { background: c.surfaceSunk, border: `1px solid ${c.border}`, borderRadius: '10px' },
  head: {
    fontSize: '13px', fontWeight: 600, color: c.textStrong,
    padding: '14px 18px', borderBottom: `1px solid ${c.border}`,
    background: c.surfaceSunk,
  },
};

// ── Buttons ────────────────────────────────────────────────────────
// The worst drift in the app: three different primaryBtn, three
// secondaryBtn, four iconBtn and three linkBtn definitions, plus eight
// one-offs (clearBtn, resetBtn, exportBtn, syncBtn, uploadBtn,
// reviewToggleBtn, pageBtn, warningBtn) that are all secondary buttons at
// slightly different sizes.
//
// Three sizes cover every real case. Primary carries a transparent border
// rather than `border: none` so a primary and a secondary sitting side by
// side are the same height — previously they differed by 2px.
const btnBase = {
  display:       'inline-flex',
  alignItems:    'center',
  justifyContent:'center',
  gap:           '7px',
  fontFamily:    'inherit',
  cursor:        'pointer',
  border:        '1px solid transparent',
  whiteSpace:    'nowrap',
  textDecoration:'none',
};
const btnMd = { ...btnBase, padding: '9px 19px', fontSize: '13px', fontWeight: 600, borderRadius: '8px' };
const btnSm = { ...btnBase, padding: '7px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px' };
const btnXs = { ...btnBase, padding: '5px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '6px' };

const solid   = { background: c.navy,    color: '#fff' };
const outline = { background: c.surface, color: c.textStrong, borderColor: c.borderStrong };

export const btn = {
  primary:     { ...btnMd, ...solid },
  primarySm:   { ...btnSm, ...solid },
  primaryXs:   { ...btnXs, ...solid },

  secondary:   { ...btnMd, ...outline },
  secondarySm: { ...btnSm, ...outline },
  secondaryXs: { ...btnXs, ...outline },

  // Destructive and cautionary actions. Both stay outline-weight: this app
  // deletes contracts and mappings, and a filled red button is too easy to
  // hit by reflex.
  danger:  { ...btnSm, background: c.surface, color: c.dangerText,  borderColor: '#FCA5A5' },
  warning: { ...btnSm, background: c.surface, color: c.warningText, borderColor: '#FCD34D' },

  // Chromeless. `icon` is a square tap target; `link` is inline text.
  icon: {
    ...btnBase, background: 'none', color: c.textMuted,
    padding: '5px', borderRadius: '6px', gap: '0',
  },
  iconBordered: {
    ...btnBase, background: 'none', color: c.textStrong,
    padding: '5px', borderRadius: '6px', gap: '0', borderColor: c.border,
  },
  // A destructive icon button must keep reading as destructive — square tap
  // target like `icon`, but the danger colour rather than neutral grey.
  iconDanger: {
    ...btnBase, background: 'none', color: c.dangerText,
    padding: '5px', borderRadius: '6px', gap: '0', borderColor: '#FCA5A5',
  },
  link: {
    ...btnBase, background: 'none', color: c.info,
    padding: 0, borderRadius: 0, fontSize: '12px', fontWeight: 500, gap: '4px',
  },
  // Inline links sitting inside dense table rows, where 12px is too loud.
  linkXs: {
    ...btnBase, background: 'none', color: c.info,
    padding: 0, borderRadius: 0, fontSize: '11px', fontWeight: 500, gap: '4px',
  },
};

// ── Elevation ──────────────────────────────────────────────────────
export const sh = {
  sm:    '0 1px 2px rgba(17,24,39,0.06)',
  md:    '0 1px 3px rgba(17,24,39,0.08), 0 4px 12px rgba(17,24,39,0.05)',
  lg:    '0 4px 24px rgba(17,24,39,0.08)',
  glass: '0 24px 70px rgba(0,0,0,0.42)',
};

export default { c, t, sp, r, sh, chart, tbl, card, btn };
